// Domain errors
export class PaymentError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PaymentError';
  }
}

export type PaymentRequestFailure = 'http' | 'network' | 'timeout' | 'bad-response';

/**
 * Falla de una llamada al middleware. `outcomeUnknown` indica que el pedido pudo haber
 * llegado al backend (timeout / corte de red en `initiate`): NO se debe reintentar a ciegas
 * porque puede generar un cobro duplicado.
 */
export class PaymentRequestError extends PaymentError {
  readonly failure: PaymentRequestFailure;
  readonly status?: number;
  readonly outcomeUnknown: boolean;
  /** Códigos/títulos de error que devolvió backend (sin secretos), p. ej. `...initiate.not-approved`. */
  readonly detail?: string;

  constructor(
    message: string,
    options: {
      failure: PaymentRequestFailure;
      status?: number;
      outcomeUnknown?: boolean;
      detail?: string;
    },
  ) {
    super(message);
    this.name = 'PaymentRequestError';
    this.failure = options.failure;
    this.status = options.status;
    this.outcomeUnknown = options.outcomeUnknown ?? false;
    this.detail = options.detail;
  }
}

export type CardTokenizationFailure =
  | 'sdk-unavailable'
  | 'timeout'
  | 'invalid-card'
  | 'unknown';

/** Falla al tokenizar una tarjeta nueva con el SDK de Payway. */
export class CardTokenizationError extends PaymentError {
  readonly failure: CardTokenizationFailure;
  /** Dato rechazado por el SDK (p. ej. `card_number`), si lo informa. */
  readonly field?: string;

  constructor(failure: CardTokenizationFailure, options: { field?: string } = {}) {
    super(`Card tokenization failed (${failure})`);
    this.name = 'CardTokenizationError';
    this.failure = failure;
    this.field = options.field;
  }
}

/** Falla de validación local de los datos de una tarjeta nueva. */
export class NewCardValidationError extends PaymentError {
  readonly fields: readonly NewCardField[];

  constructor(fields: readonly NewCardField[]) {
    super(`Invalid new card data: ${fields.join(', ')}`);
    this.name = 'NewCardValidationError';
    this.fields = fields;
  }
}

export type NewCardField = 'cardNumber' | 'expiration' | 'holderName' | 'securityCode' | 'document';

export type PaymentTokenFailure = 'missing-cvv' | 'expired';

/**
 * No se pudo preparar el token de pago ANTES de llamar a `initiate` (no hubo cobro, es seguro
 * reintentar): falta el CVV de una tarjeta guardada o venció el token de una tarjeta nueva.
 */
export class PaymentTokenError extends PaymentError {
  readonly reason: PaymentTokenFailure;

  constructor(reason: PaymentTokenFailure) {
    super(`Payment token not available (${reason})`);
    this.name = 'PaymentTokenError';
    this.reason = reason;
  }
}
